import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SOURCE_LABELS } from "../../shared/sources";
import { useScanActivity } from "../scanContext";

const IDLE_PERIOD = 6.8;
const SCAN_PERIOD = 3.2;

type Blip = {
  key: string;
  x: number;
  y: number;
  deg: number;
  label: string;
  count: number;
  strength: number;
};

function placeBlips(sources: { source: string; count: number }[]): Blip[] {
  const clean = sources
    .filter((s) => s.source !== "demo" && s.count > 0)
    .slice(0, 8);
  const max = Math.max(1, ...clean.map((s) => s.count));
  const n = Math.max(clean.length, 1);
  return clean.map((s, i) => {
    const angle = -Math.PI / 2 + (i / n) * Math.PI * 2;
    const ring = 0.52 + (i % 2) * 0.16 + (s.count / max) * 0.06;
    const r = 38 * Math.min(0.92, ring);
    const deg = ((angle * 180) / Math.PI + 90 + 360) % 360;
    return {
      key: s.source,
      x: 50 + Math.cos(angle) * r,
      y: 50 + Math.sin(angle) * r,
      deg,
      label: SOURCE_LABELS[s.source] || s.source,
      count: s.count,
      strength: 0.55 + 0.45 * (s.count / max),
    };
  });
}

/** Phosphor residual: bright at beam, fades over ~0.55 of a turn. */
function phosphor(sweepDeg: number, blipDeg: number): number {
  let d = (sweepDeg - blipDeg + 360) % 360;
  if (d > 200) return 0.12;
  const t = 1 - d / 200;
  return 0.12 + t * t * 0.88;
}

function useCountUp(target: number, active: boolean, durationMs = 1400) {
  const [value, setValue] = useState(0);
  const prev = useRef(0);

  useEffect(() => {
    const from = prev.current;
    const to = Math.max(0, target || 0);
    if (!active && from === to) {
      setValue(to);
      return;
    }
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / durationMs);
      const eased = 1 - (1 - p) ** 3;
      const next = Math.round(from + (to - from) * eased);
      setValue(next);
      if (p < 1) raf = requestAnimationFrame(tick);
      else prev.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, durationMs]);

  return value;
}

export function Radar({
  sources,
  total,
}: {
  sources: { source: string; count: number }[];
  total?: number;
}) {
  const uid = useId().replace(/:/g, "");
  const { scanning } = useScanActivity();
  const blips = useMemo(() => placeBlips(sources), [sources]);
  const live = blips.length > 0;
  const targetTotal =
    total != null ? total : live ? blips.reduce((a, b) => a + b.count, 0) : 0;

  const [sweepDeg, setSweepDeg] = useState(0);
  const [nowMs, setNowMs] = useState(0);
  const [hitFlash, setHitFlash] = useState<string | null>(null);
  const lastHit = useRef<Record<string, number>>({});
  const reducedMotion = useRef(false);

  useEffect(() => {
    reducedMotion.current = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
  }, []);

  useEffect(() => {
    if (reducedMotion.current) return;
    let raf = 0;
    const t0 = performance.now();
    const loop = (t: number) => {
      const period = scanning ? SCAN_PERIOD : IDLE_PERIOD;
      const deg = (((t - t0) / 1000 / period) * 360) % 360;
      setSweepDeg(deg);
      setNowMs(t);

      for (const b of blips) {
        let d = (deg - b.deg + 360) % 360;
        if (d < 3.5) {
          const prev = lastHit.current[b.key] || 0;
          if (t - prev > period * 700) {
            lastHit.current[b.key] = t;
            setHitFlash(b.key);
          }
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [scanning, blips]);

  useEffect(() => {
    if (!hitFlash) return;
    const id = window.setTimeout(() => setHitFlash(null), 420);
    return () => clearTimeout(id);
  }, [hitFlash]);

  const displayTotal = useCountUp(targetTotal, live || scanning, scanning ? 2200 : 1100);

  const ticks = useMemo(
    () =>
      Array.from({ length: 72 }, (_, i) => {
        const a = (i / 72) * Math.PI * 2 - Math.PI / 2;
        const major = i % 6 === 0;
        const mid = i % 3 === 0;
        const outer = 44.7;
        const inner = major ? 41.2 : mid ? 42.6 : 43.5;
        return {
          x1: 50 + Math.cos(a) * inner,
          y1: 50 + Math.sin(a) * inner,
          x2: 50 + Math.cos(a) * outer,
          y2: 50 + Math.sin(a) * outer,
          major,
        };
      }),
    [],
  );

  // Occasional noise contacts while scanning / live
  const noise = useMemo(() => {
    if (!scanning && !live) return [];
    const seed = Math.floor(nowMs / 900);
    return Array.from({ length: scanning ? 5 : 2 }, (_, i) => {
      const a =
        ((seed * 47 + i * 71) % 360) * (Math.PI / 180) - Math.PI / 2;
      const r = 16 + ((seed * 13 + i * 19) % 24);
      return {
        key: `n-${i}`,
        x: 50 + Math.cos(a) * r,
        y: 50 + Math.sin(a) * r,
        opacity: 0.15 + ((seed + i) % 5) * 0.06,
      };
    });
  }, [nowMs, scanning, live]);

  const status = scanning ? "сканирование" : live ? "сигналов" : "ожидание";
  const period = scanning ? SCAN_PERIOD : IDLE_PERIOD;

  return (
    <div
      className={`radar-block${scanning ? " is-scanning" : ""}${live ? " is-live" : ""}`}
      style={{ ["--radar-period" as string]: `${period}s` }}
    >
      <div className={`radar-wrap${live || scanning ? " is-live" : ""}`} aria-hidden>
        <div className="radar-plate" />
        <div className="radar-halo" />
        <div className="radar-scanlines" />
        {scanning ? <div className="radar-active-ring" /> : null}

        <svg className="radar-svg" viewBox="0 0 100 100">
          <defs>
            <radialGradient id={`rg-disc-${uid}`} cx="50%" cy="48%" r="52%">
              <stop offset="0%" className="radar-stop-core" />
              <stop offset="55%" className="radar-stop-mid" />
              <stop offset="100%" className="radar-stop-edge" />
            </radialGradient>
            <linearGradient
              id={`rg-sweep-${uid}`}
              gradientUnits="userSpaceOnUse"
              x1="50"
              y1="50"
              x2="50"
              y2="6"
            >
              <stop offset="0%" className="radar-sweep-a" />
              <stop offset="45%" className="radar-sweep-b" />
              <stop offset="100%" className="radar-sweep-c" />
            </linearGradient>
            <radialGradient id={`rg-ping-${uid}`} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0" />
              <stop offset="72%" stopColor="currentColor" stopOpacity="0" />
              <stop offset="88%" className="radar-ping-stop" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </radialGradient>
            <filter id={`rg-glow-${uid}`} x="-80%" y="-80%" width="260%" height="260%">
              <feGaussianBlur stdDeviation="1.15" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <clipPath id={`rg-clip-${uid}`}>
              <circle cx="50" cy="50" r="44.5" />
            </clipPath>
          </defs>

          <circle
            cx="50"
            cy="50"
            r="45"
            className="radar-disc"
            fill={`url(#rg-disc-${uid})`}
          />
          <circle cx="50" cy="50" r="44.5" className="radar-ring-outer" fill="none" />

          <g clipPath={`url(#rg-clip-${uid})`}>
            <circle
              cx="50"
              cy="50"
              r="18"
              className="radar-ping"
              fill={`url(#rg-ping-${uid})`}
              style={{ animationDuration: `${period}s` }}
            />
            <circle
              cx="50"
              cy="50"
              r="18"
              className="radar-ping"
              fill={`url(#rg-ping-${uid})`}
              style={{
                animationDuration: `${period}s`,
                animationDelay: `${period / 2}s`,
              }}
            />

            {ticks.map((t, i) => (
              <line
                key={i}
                x1={t.x1}
                y1={t.y1}
                x2={t.x2}
                y2={t.y2}
                className={t.major ? "radar-tick major" : "radar-tick"}
              />
            ))}

            {[14, 24, 34, 42].map((r) => (
              <circle key={r} cx="50" cy="50" r={r} fill="none" className="radar-ring" />
            ))}

            <line x1="50" y1="5.5" x2="50" y2="94.5" className="radar-axis" />
            <line x1="5.5" y1="50" x2="94.5" y2="50" className="radar-axis" />

            {noise.map((n) => (
              <circle
                key={n.key}
                cx={n.x}
                cy={n.y}
                r="0.7"
                className="radar-noise"
                style={{ opacity: n.opacity }}
              />
            ))}

            <g
              className="radar-sweep-driven"
              filter={`url(#rg-glow-${uid})`}
              style={{ transform: `rotate(${sweepDeg}deg)` }}
            >
              <path
                className="radar-sweep-trail"
                d="M50 50 L50 8 A42 42 0 0 0 12 35 Z"
              />
              <path
                className="radar-sweep-wedge"
                d="M50 50 L50 6.5 A43.5 43.5 0 0 1 78 18.5 Z"
                fill={`url(#rg-sweep-${uid})`}
              />
              <line x1="50" y1="50" x2="50" y2="6.2" className="radar-beam" />
              <circle cx="50" cy="6.8" r="1.25" className="radar-beam-tip" />
            </g>

            {blips.map((b) => {
              const ph = phosphor(sweepDeg, b.deg);
              const lit = hitFlash === b.key;
              const op = Math.min(1, ph * b.strength * (lit ? 1.35 : 1));
              return (
                <g key={b.key} className={`radar-blip${lit ? " is-hit" : ""}`}>
                  <circle
                    cx={b.x}
                    cy={b.y}
                    r={lit ? 6.5 : 4.8}
                    className="radar-blip-ring"
                    style={{
                      opacity: lit ? 0.85 : op * 0.35,
                      transformOrigin: `${b.x}px ${b.y}px`,
                    }}
                  />
                  <circle
                    cx={b.x}
                    cy={b.y}
                    r={1.1 + op * 1.4}
                    className="radar-blip-glow"
                    style={{ opacity: op * 0.55 }}
                  />
                  <circle
                    cx={b.x}
                    cy={b.y}
                    r={0.85 + op * 0.55}
                    className="radar-blip-core"
                    style={{ opacity: 0.35 + op * 0.65 }}
                  />
                </g>
              );
            })}
          </g>

          <circle cx="50" cy="50" r="6.2" className="radar-core-ring" fill="none" />
          <circle cx="50" cy="50" r="2.1" className="radar-core" />
        </svg>

        <div className="radar-center">
          <strong className={scanning ? "is-ticking" : ""}>{displayTotal || "—"}</strong>
          <span>{status}</span>
        </div>
      </div>

      {blips.length ? (
        <ul className="radar-legend">
          {blips.map((b) => {
            const ph = phosphor(sweepDeg, b.deg);
            const hot = ph > 0.72 || hitFlash === b.key;
            return (
              <li key={b.key}>
                <Link
                  className={`radar-legend-link${hot ? " is-hot" : ""}`}
                  to={`/mentions?source=${encodeURIComponent(b.key)}`}
                  title={`Открыть упоминания: ${b.label}`}
                >
                  <i />
                  <span>{b.label}</span>
                  <em>{b.count}</em>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="radar-empty">
          {scanning ? "Идёт обход источников…" : "Нет активных источников в окне"}
        </p>
      )}
    </div>
  );
}
